import { createElement, useCallback, useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  setOpen as setConfirmDialogOpen,
  setContent,
  setOnChange,
  setOptions,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import {
  type OptOutAnswer,
  OptOutReason,
  isOptOutAnswerComplete
} from "features/app-layout/scheduler-choice/opt-out-reason.component";
import {
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";
import { schedulingSettingsApi } from "features/scheduling-v3/data/api/scheduling-settings.api";

import {
  type SideMenuSwitchSource,
  trackSideMenuOptIn,
  trackSideMenuOptOut
} from "./side-menu-choice-telemetry";
import {
  SIDE_MENU_NAMESPACE,
  SIDE_MENU_OPT_OUT_OPTIONS,
  type SideMenuOptOutReasonKey,
  writeSideMenuOptOut,
  writeUseClassic
} from "./side-menu-document";
import { useSideMenuChoice } from "./use-side-menu-choice.hook";

const SWITCH_FAILED = "Couldn't switch menus. Try again.";

/**
 * ENG-2535: every way of switching menus goes through here — the classic rail's "Try the
 * new menu", the new menu's "Classic menu" row and the account menu. A switch is saved
 * first; only a saved switch changes what renders (through the choice hook's cache) and is
 * recorded. Going back asks for a reason, like the scheduler (ENG-2210).
 */
export function useSwitchSideMenu() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const practice = useAppSelector(selectCurrentPractice);
  const { document, fromServer } = useSideMenuChoice();
  const [putSettings] =
    schedulingSettingsApi.usePutSchedulingSettingsMutation();

  // Read at the moment of writing, not when a dialog opened. Updated in an effect, never
  // during render, so a render React discards cannot leak into the ref.
  const latest = useRef({ document, fromServer });
  useEffect(() => {
    latest.current = { document, fromServer };
  }, [document, fromServer]);

  /**
   * Every write builds on the server's own record: when the choice came from the mirror or
   * a failed read, it is read first; if it cannot be, nothing is written, because writing
   * the stand-in back would erase every reason the person gave.
   */
  const save = useCallback(
    async (
      build: (base: unknown) => Record<string, unknown>
    ): Promise<boolean> => {
      const userId = user?.id;
      const practiceId = practice?.id;
      if (userId == null || practiceId == null) return false;
      try {
        const base = latest.current.fromServer
          ? latest.current.document
          : await dispatch(
              schedulingSettingsApi.endpoints.getSideMenuChoice.initiate(
                { userId, practiceId },
                { subscribe: false, forceRefetch: true }
              )
            ).unwrap();
        const saved = await putSettings({
          namespace: SIDE_MENU_NAMESPACE,
          practiceId,
          document: build(base)
        }).unwrap();
        dispatch(
          schedulingSettingsApi.util.upsertQueryData(
            "getSideMenuChoice",
            { userId, practiceId },
            saved.document
          )
        );
        return true;
      } catch {
        dispatch(setCustomMessage(SWITCH_FAILED));
        dispatch(setSnackbarOpen(true));
        return false;
      }
    },
    [dispatch, putSettings, user?.id, practice?.id]
  );

  const switchToNew = useCallback(
    async (source: SideMenuSwitchSource) => {
      const now = new Date().toISOString();
      if (await save((base) => writeUseClassic(base, false, now))) {
        trackSideMenuOptIn(source);
      }
    },
    [save]
  );

  const switchToClassic = useCallback(
    (source: SideMenuSwitchSource) => {
      let answer: OptOutAnswer<SideMenuOptOutReasonKey> = {
        reason: "",
        detail: ""
      };
      // The reason is required, so "Switch to classic" stays disabled until the answer is
      // complete. The dialog's content never re-renders (it lives in the store), so the
      // button is enabled from here, through the dialog's options.
      let disabled = true;
      const showOptions = () =>
        dispatch(
          setOptions({
            actionButtonText: "Switch to classic",
            actionButtonDisabled: disabled
          })
        );
      const confirm = async () => {
        if (!isOptOutAnswerComplete(answer)) return;
        const { reason, detail } = answer;
        const saved = await save((base) =>
          writeSideMenuOptOut(base, {
            at: new Date().toISOString(),
            practiceId: practice?.id ?? 0,
            reason,
            detail,
            source
          })
        );
        if (saved) trackSideMenuOptOut(source, reason);
      };
      dispatch(setTitle("Switch back to the classic menu?"));
      dispatch(
        setContent(
          createElement(OptOutReason, {
            options: SIDE_MENU_OPT_OUT_OPTIONS,
            question: "What made you switch back to the classic menu?",
            onChange: (next: OptOutAnswer<SideMenuOptOutReasonKey>) => {
              answer = next;
              const nextDisabled = !isOptOutAnswerComplete(next);
              if (nextDisabled === disabled) return;
              disabled = nextDisabled;
              showOptions();
            }
          })
        )
      );
      showOptions();
      dispatch(setOnChange(confirm));
      dispatch(setConfirmDialogOpen(true));
    },
    [dispatch, practice?.id, save]
  );

  return { switchToNew, switchToClassic };
}
